import React, { useEffect, useState } from 'react';
import './App.scss';
import { Routes, Route, BrowserRouter, useLocation } from 'react-router-dom'
import Menu from './menu/menu';
import Home from './home/home';
//import Login from './login/login';
import ProtectedRoute from "./shared/protected-route";
import * as ActivityLogger from "./shared/activity-logger";
import * as Data from './login/data';
import { SamlAuthService } from './auth/saml-auth-service';
import AuthError from './auth/AuthError';
import LoggedOut from './auth/LoggedOut';
import InactivityManager from './auth/inactivity-manager';
import Unauthorized from './auth/unauthorized';
import { TokenManager } from './auth/token-manager';
//import * as dataRequestService from "./shared/data-request-service"

const tokenManager = TokenManager.getInstance();
//const dataService = dataRequestService.GetDataRequestService();

function App() {
    //const tokenRepository = new TokenRepository();
    //const token = tokenRepository.getToken(); // BLR

    const [authState, setAuthState] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

    const InterCompanyBilling = React.lazy(() => import('./fileExplorer/report-explorer'));
    const Morv2 = React.lazy(() => import('./fileExplorer/report-explorer'));
    const Volrate = React.lazy(() => import('./volrate/volrate'));
    const ContactUsAdmin = React.lazy(() => import('./contactUsAdmin/contact-us-admin'));    
    const APGL = React.lazy(() => import("./apglenvi/apgl"));
    const UserAdmin = React.lazy(() => import("./useradmin/user-admin"));
    const QualityIndicators = React.lazy(() => import("./qualityIndicators/qualityIndicators"));
    const RevenueWorkbook = React.lazy(() => import("./revenueworkbook/revenue-workbook"));
    const MedicareEOY = React.lazy(() => import('./datapointreport/data-point-report'));
    const OWR = React.lazy(() => import('./datapointreport/data-point-report'));
    const DataPointAdmin = React.lazy(() => import('./datapointreportAdmin/data-point-report-admin'));
    const FacilityOnboarding = React.lazy(() => import("./facilityonboarding/facility-onboarding"));
    const NSH = React.lazy(() => import("./nsh/nsh"));
    const Account = React.lazy(() => import("./account/account"));    
    const Administration = React.lazy(() => import('./administration/administration'));
    const CptSpecMap = React.lazy(() => import('./cptspecmap/cpt-spec-map'));
    const RecordLookup = React.lazy(()=> import("./recordLookup/recordLookup"));
    const SpecialtyMapping = React.lazy(() => import("./specialtyMapping/specialtyMapping"));
    const EtlJob = React.lazy(() => import("./etlJob/etlJob"));
    const Ultipro = React.lazy(() => import("./Ultipro/ultipro"));
    const EtlMapping = React.lazy(() => import("./etlMapping/etlMapping"));
    const PatientLookup = React.lazy(() => import("./patientLookup/PatientLookup"));
    const RawFinancialDataExport = React.lazy(() => import("./rawFinancialData/RawFinancialDataExport"));
    const EDW2 = React.lazy(() => import("./edw2/edw2"));

    useEffect(() => {
        const initAuth = async () => {
            // Check if we're on the callback page
            const urlParams = new URLSearchParams(window.location.search);
            const tokenFromUrl = urlParams.get('token');

            if (tokenFromUrl) {
                // We're coming back from Azure with a new token
                console.log('New token from Azure callback');
                tokenManager.setToken(tokenFromUrl);
                setAuthState('authenticated');
                // Clean up URL
                window.history.replaceState({}, '', '/');
                return;
            }

            // Check for existing token in storage
            const token = tokenManager.getToken();
            console.log('Token from storage:', token ? 'EXISTS' : 'NULL');

            if (!token) {
                console.log('No token, setting unauthenticated');
                setAuthState('unauthenticated');
                return;
            }

            try {
                const parts = token.split('.');
                if (parts.length !== 3) {
                    console.error('Invalid JWT format');
                    tokenManager.clearToken();
                    setAuthState('unauthenticated');
                    return;
                }

                const payload = JSON.parse(atob(parts[1]));
                const expiresAt = payload.exp * 1000;
                const now = Date.now();

                console.log('Token expires at:', new Date(expiresAt).toISOString());

                if (now >= expiresAt) {
                    console.log('Token expired, attempting refresh...');
                    const newToken = await tokenManager.refreshAccessToken();

                    if (newToken) {
                        console.log('Token refreshed successfully');
                        setAuthState('authenticated');
                    } else {
                        console.log('Token refresh failed');
                        tokenManager.clearToken();
                        setAuthState('unauthenticated');
                    }
                } else {
                    console.log('Token is valid, setting authenticated');
                    setAuthState('authenticated');
                }
            } catch (error) {
                console.error('Error checking token:', error);
                tokenManager.clearToken();
                setAuthState('unauthenticated');
            }
        };

        initAuth();
    }, []);

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'tokens') {
                if (!e.newValue) {
                    setAuthState('unauthenticated');
                } else {
                    try {
                        const parsed = JSON.parse(e.newValue);
                        if (parsed.accessToken) {
                            setAuthState('authenticated');
                        }
                    } catch {
                        setAuthState('unauthenticated');
                    }
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // Loading spinner
    if (authState === 'loading') {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <div className="text-center">
                    <div className="spinner-border mb-3" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div>Checking authentication status...</div>
                </div>
            </div>
        );
    }

    const hasUserPermission = async (appRefName: string): Promise<boolean> => {
        const result = await Data.GetUserRolesAndPermissions(appRefName);
        return result && result.success;
    };

    const isUserAdmin = async (): Promise<boolean> => {
        return await Data.GetUserAdminInfo();
    };

    return (
        <BrowserRouter>
            <div className="App">
                {authState === 'authenticated' && 
                    <>
                    <RouteTracking />
                    <Menu />
                    </>
                }
            
                <div id="module-root">                    
                    <Routes>
                        <Route path="/saml/error" element={<AuthError />} />
                        <Route path="/logged-out" element={<LoggedOut />} />
                        <Route path="/saml/unauthorized" element={<Unauthorized />} />

                        {authState === 'authenticated' ? (
                            <Route path="*" element={
                                <InactivityManager
                                    idleTimeout={15 * 60 * 1000} // 15 minutes
                                    warningDuration={60} // 60 seconds warning
                                >
                                    <Routes>
                                        <Route
                                            index
                                            element={
                                                <Home />
                                            }
                                        />
                                        <Route
                                            path="/ApglEnviV3/*"
                                            element={
                                                <ProtectedRoute key={"ApglEnviV3"} permissionCheck={hasUserPermission} appRefName={"ApglEnviV3"} appName={"APGL ENVI"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <APGL id="ApglEnviV3" appRefName="ApglEnviV3" title="APGL-ENVI" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/IntercompanyBillingV3"
                                            element={
                                                <ProtectedRoute key={"IntercompanyBillingV3"} permissionCheck={hasUserPermission} appRefName={"IntercompanyBillingV3"} appName={"Intercompany Billing"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <InterCompanyBilling id="IntercompanyBillingV3" key="IntercompanyBillingV3" appRefName="IntercompanyBillingV3" title="Intercompany Billing" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/MorV3"
                                            element={
                                                <ProtectedRoute key={"MorV3"} permissionCheck={hasUserPermission} appRefName={"MorV3"} appName={"Monthly Operating Reports"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <Morv2 id="MorV3" appRefName="MorV3" key="MorV3" title="Monthly Operating Reports" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/VolRateV3"
                                            element={
                                                <ProtectedRoute key={"VolRateV3"} permissionCheck={hasUserPermission} appRefName={"VolRateV3"} appName={"Volume / Rate Analysis"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <Volrate id="VolRateV3" appRefName="VolRateV3" title="Volume Rate" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/ContactUsAdminV3"
                                            element={
                                                <ProtectedRoute key={"ContactUsAdminV3"} permissionCheck={hasUserPermission} appRefName={"ContactUsAdminV3"} appName={"Contact Us Admin"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <ContactUsAdmin id="ContactUsAdminV3" appRefName="ContactUsAdminV3" title="Contact Us Admin" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/UserAdminV3/*"
                                            element={
                                                <ProtectedRoute key={"UserAdminV3"} permissionCheck={isUserAdmin} appRefName={"UserAdminV3"} appName={"User Admin"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <UserAdmin id="UserAdminV3" appRefName="UserAdminV3" title="User Admin" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/QualityIndicatorsV3/*"
                                            element={
                                                <ProtectedRoute key={"QualityIndicatorsV3"} permissionCheck={hasUserPermission} appRefName={"QualityIndicatorsV3"} appName={"Clincal Quality Indicators"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <QualityIndicators id="QualityIndicatorsV3" appRefName="QualityIndicatorsV3" title="Quality Indicators" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/RevenueWorkbookV3/*"
                                            element={
                                                <ProtectedRoute key={"RevenueWorkbookV3"} permissionCheck={hasUserPermission} appRefName={"RevenueWorkbookV3"} appName={"Revenue Workbook"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <RevenueWorkbook id="RevenueWorkbookV3" appRefName="RevenueWorkbookV3" title="Revenue Workbook" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/MedicareEoyV3/*"
                                            element={
                                                <ProtectedRoute key={"MedicareEoyV3"} permissionCheck={hasUserPermission} appRefName={"MedicareEoyV3"} appName={"Medicare EOY"}>
                                                    <React.Suspense fallback={<React.Fragment>...Loading...</React.Fragment>}>
                                                        <MedicareEOY id="MedicareEoyV3" key="MedicareEoyV3" appRefName="MedicareEoyV3" title="Medicare EOY" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/OperationsWeeklyReportV3/*"
                                            element={
                                                <ProtectedRoute key={"OperationsWeeklyReportV3"} permissionCheck={hasUserPermission} appRefName={"OperationsWeeklyReportV3"} appName={"Operations Weekly Report"}>
                                                    <React.Suspense fallback={<React.Fragment>...Loading...</React.Fragment>}>
                                                        <OWR id="OWR" appRefName="OperationsWeeklyReportV3" key="OperationsWeeklyReportV3" title="Operations Weekly Report" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/DataPointAdminV3/*"
                                            element={
                                                <ProtectedRoute key={"DataPointAdminV3"} permissionCheck={hasUserPermission} appRefName={"DataPointAdminV3"} appName={"Data Point Admin"}>
                                                    <React.Suspense fallback={<React.Fragment>...Loading...</React.Fragment>}>
                                                        <DataPointAdmin id="DataPointAdminV3" appRefName="DataPointAdminV3" title="Data Point Report Admin" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/FacilityOnboardingV3/*"
                                            element={
                                                <ProtectedRoute key={"FacilityOnboardingV3"} permissionCheck={hasUserPermission} appRefName={"FacilityOnboardingV3"} appName={"Facility Onboarding"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <FacilityOnboarding id="FacilityOnboardingV3" appRefName="FacilityOnboardingV3" title="Facility Onboarding" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/EncounterExpectedReimbursement/*"
                                            element={
                                                <ProtectedRoute key={"EncounterExpectedReimbursement"} permissionCheck={hasUserPermission} appRefName={"EncounterExpectedReimbursement"} appName={"Expected Reimbursement"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <NSH id="EncounterExpectedReimbursement" appRefName="EncounterExpectedReimbursement" title="Encounter Expected Reimbursement" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/CptSpecialtyMappingV3/*"
                                            element={
                                                <ProtectedRoute key={"CptSpecialtyMappingV3"} permissionCheck={hasUserPermission} appRefName={"CptSpecialtyMappingV3"} appName={"CPT Specialty Mapping"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <CptSpecMap id="CptSpecialtyMappingV3" appRefName="CptSpecialtyMappingV3" title="CPT Specialty Mapping" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/Account/*"
                                            element={
                                                <React.Suspense fallback={<>...Loading...</>}>
                                                    <Account />
                                                </React.Suspense>
                                            }
                                        />
                                        <Route
                                            path="/AdministrationV3/*"
                                            element={
                                                <React.Suspense fallback={<>...Loading...</>}>
                                                    <Administration id="AdministrationV3" appRefName="AdministrationV3" title="Administration" />
                                                </React.Suspense>
                                            }
                                        />
                                        <Route
                                            path="/RecordLookupV3/*"
                                            element={
                                                <ProtectedRoute key={"RecordLookupV3"} permissionCheck={hasUserPermission} appRefName={"RecordLookupV3"} appName={"Legacy Record Lookup"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <RecordLookup id="RecordLookupV3" appRefName="RecordLookupV3" title="Legacy Record Lookup" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/SpecialtyMappingV3/*"
                                            element={
                                                <ProtectedRoute key={"SpecialtyMappingV3"} permissionCheck={hasUserPermission} appRefName={"SpecialtyMappingV3"} appName={"Specialty Mapping"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <SpecialtyMapping id="SpecialtyMappingV3" appRefName="SpecialtyMappingV3" title="Specialty Mapping" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/ETLJobV3/*"
                                            element={
                                                <ProtectedRoute key={"ETLJobV3"} permissionCheck={hasUserPermission} appRefName={"ETLJobV3"} appName={"ETL Job"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <EtlJob id="ETLJobV3" appRefName="ETLJobV3" title="ETL Job" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/UltiProManageMappingsV3/*"
                                            element={
                                                <ProtectedRoute key={"UltiProManageMappingsV3"} permissionCheck={hasUserPermission} appRefName={"UltiProManageMappingsV3"} appName={"UltiPro Manage Mappings"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <Ultipro id="UltiProManageMappingsV3" appRefName="UltiProManageMappingsV3" title="Manage Missing Mappings" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/EtlMappingV3/*"
                                            element={
                                                <ProtectedRoute key={"ETLMappingV3"} permissionCheck={hasUserPermission} appRefName={"ETLMappingV3"} appName={"ETL Mapping"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <EtlMapping id="ETLMappingV3" appRefName="ETLMappingV3" title="ETL Mapping" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/PatientLookupV3/*"
                                            element={
                                                <ProtectedRoute key={"PatientLookUpV3"} permissionCheck={hasUserPermission} appRefName={"PatientLookUpV3"} appName={"Patient Lookup"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <PatientLookup id="PatientLookupV3" appRefName="PatientLookupV3" title="Patient-lookup" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/RawFinancialDataV3/*"
                                            element={
                                                <ProtectedRoute key={"RawFinancialDataV3"} permissionCheck={hasUserPermission} appRefName={"RawFinancialDataV3"} appName={"Raw Financial Data"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <RawFinancialDataExport id="RawFinancialDataV3" appRefName="RawFinancialDataV3" title="Raw Financial Data" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                        <Route
                                            path="/EDW20V3/*"
                                            element={
                                                <ProtectedRoute key={"EDW20V3"} permissionCheck={hasUserPermission} appRefName={"EDW20V3"} appName={"EDW2.0"}>
                                                    <React.Suspense fallback={<>...Loading...</>}>
                                                        <EDW2 id="EDW20" appRefName="EDW20V3" title="EDW2" />
                                                    </React.Suspense>
                                                </ProtectedRoute>
                                            }
                                        />
                                    </Routes>
                                </InactivityManager>} />
                        ) : (
                                <Route path="*" element={<UnauthenticatedHandler />} />
                        )}
                    </Routes>
                </div>
            </div>
        </BrowserRouter>            
    );
}

// Separate component to handle unauthenticated users
const UnauthenticatedHandler: React.FC = () => {
    const authService = new SamlAuthService();

    useEffect(() => {
        const path = window.location.pathname;

        // Only redirect if not on auth-related pages
        if (!path.includes('/saml/') && !path.includes('/logout') && !path.includes('/logged-out')) {
            console.log('No valid token found, redirecting to SAML login');
            authService.login();
        }
    }, []);

    return (
        <div className="d-flex justify-content-center align-items-center min-vh-100">
            <div className="text-center">
                <div className="spinner-border mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div>Redirecting to login...</div>
            </div>
        </div>
    );
};

const RouteTracking = () => {
    const location = useLocation();

    const getRouteIfNotTaken = () => {
        const route = location.pathname.split('/')[1];        
        if (!route) {
            return;
        }

        //const routesTaken = sessionStorage.getItem('routesTaken');
        //if (routesTaken) {
        //    const routes = JSON.parse(routesTaken);
        //    if (routes && routes.includes(route)) {
        //        return null;
        //    }
        //}
        return route;
    }

    //const storeRoute = (route: string) => {
    //    const routesTaken = sessionStorage.getItem('routesTaken');
    //    if (!routesTaken)
    //        sessionStorage.setItem('routesTaken', JSON.stringify([route]));
    //    else {
    //        const routes = JSON.parse(routesTaken)
    //        routes.push(route);
    //        sessionStorage.setItem('routesTaken', JSON.stringify(routes));
    //    }
    //}

    useEffect(() => {

        // Whenever the route changes, track the route usage by making an API call
        const route = getRouteIfNotTaken();
        if (route) {
            //storeRoute(route);
            ActivityLogger.LogUserActivity(route);
        }
    }, [location.pathname]);

    //useEffect(() => {
    //    // Don't track auth-related pages
    //    const publicPaths = ['/saml/', '/logout', '/logged-out', '/unauthorized'];
    //    if (publicPaths.some(path => location.pathname.includes(path))) {
    //        return;
    //    }

    //    const route = location.pathname.split('/')[1];
    //    if (route) {
    //        ActivityLogger.LogUserActivity(route);
    //    }
    //}, [location.pathname]);

    return null; // This component doesn't render anything
};

export default App;
